import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AppState } from '@/types';
import { 
  generateProjectionRows, 
  DEFAULT_ENERGY_PER_TX,
  DEFAULT_BANDWIDTH_PER_TX,
  DEFAULT_ENERGY_PER_TRX,
  DEFAULT_BANDWIDTH_PER_TRX
} from '@/lib/calculations';
import { fetchChainParameters, cacheChainParameters, getCachedChainParameters } from '@/lib/api/tron';
import { fetchTrxPrice, fetchTrxPriceAlternative, cacheTrxPrice, getCachedTrxPrice } from '@/lib/api/coingecko';

const DEFAULT_STATE = {
  recipientType: 'existing' as const,
  startTpd: 10,
  dailyInc: 1,
  days: 30,
  energyPerTx: DEFAULT_ENERGY_PER_TX.existing,
  bwPerTx: DEFAULT_BANDWIDTH_PER_TX,
  energyPerTrx: DEFAULT_ENERGY_PER_TRX,
  bwPerTrx: DEFAULT_BANDWIDTH_PER_TRX,
  sigCount: 1,
  trxPrice: 0.12,
  usdtContract: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
  endpoint: 'https://api.trongrid.io',
  apiKey: undefined,
  availEnergy: undefined,
  availBandwidth: undefined,
  energyUnitSun: 420, // Provide realistic fallback value
  bandwidthUnitSun: 1000, // Provide realistic fallback value
  rows: [],
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_STATE,

      set: (updates) => {
        set(updates);
        // Auto-compute when inputs change
        setTimeout(() => get().compute(), 0);
      },

      compute: () => {
        const state = get();
        const rows = generateProjectionRows(
          state.startTpd,
          state.dailyInc,
          state.days,
          state.recipientType,
          state.energyPerTx,
          state.bwPerTx,
          state.energyPerTrx,
          state.bwPerTrx,
          state.availEnergy,
          state.availBandwidth,
          state.energyUnitSun,
          state.bandwidthUnitSun,
          state.trxPrice
        );

        set({ rows });
      },

      fetchChainParams: async () => {
        const state = get();
        
        try {
          // Try to get cached parameters first
          const cached = getCachedChainParameters();
          if (cached) {
            set({ 
              energyUnitSun: cached.energyUnitSun, 
              bandwidthUnitSun: cached.bandwidthUnitSun 
            });
            get().compute();
          }

          // Fetch fresh parameters
          const params = await fetchChainParameters(state.endpoint, state.apiKey);
          set({ 
            energyUnitSun: params.energyUnitSun, 
            bandwidthUnitSun: params.bandwidthUnitSun 
          });
          
          // Cache the new parameters
          cacheChainParameters(params);
          
          // Recompute with new parameters
          get().compute();
        } catch (error) {
          console.error('Failed to fetch chain parameters:', error);
          // Keep existing values if fetch fails
        }
      },

      fetchTrxPrice: async () => {
        try {
          // Try to get cached price first
          const cached = getCachedTrxPrice();
          if (cached) {
            set({ trxPrice: cached });
            get().compute();
          }

          // Try CoinGecko first
          try {
            const price = await fetchTrxPrice();
            set({ trxPrice: price });
            cacheTrxPrice(price);
            get().compute();
            return;
          } catch (error) {
            console.warn('CoinGecko failed, trying alternative APIs...');
          }

          // If CoinGecko fails, try alternative APIs
          try {
            const price = await fetchTrxPriceAlternative();
            set({ trxPrice: price });
            cacheTrxPrice(price);
            get().compute();
            return;
          } catch (error) {
            console.warn('Alternative APIs failed, using fallback price');
          }

          // If all APIs fail, use fallback price
          const fallbackPrice = 0.12;
          set({ trxPrice: fallbackPrice });
          console.log('Using fallback TRX price:', fallbackPrice);
          
        } catch (error) {
          console.error('Failed to fetch TRX price from all sources:', error);
          // Keep existing price if all sources fail
        }
      },

      exportCsv: () => {
        const state = get();
        if (state.rows.length === 0) return;

        const headers = [
          'Day',
          'Transfers/Day',
          'Energy/Day',
          'TRX for Energy',
          'Bandwidth/Day',
          'TRX for Bandwidth',
          'TRX Burned (if under-staked)',
          'Total TRX Needed',
          'Daily Cost Without Staking (TRX)',
          'Daily Savings (USD)',
          'Daily Cost (USD)',
          'Cumulative Cost (USD)',
        ];

        const csvContent = [
          headers.join(','),
          ...state.rows.map(row => [
            row.day,
            row.tpd,
            row.energyDay,
            row.trxNeeded.toFixed(6),
            row.bandwidthDay,
            row.trxForBandwidth.toFixed(6),
            row.trxBurned.toFixed(6),
            row.totalTrxNeeded.toFixed(6),
            row.dailyCostWithoutStaking.toFixed(6),
            row.savingsVsNoStake.toFixed(2),
            row.dailyCost.toFixed(2),
            row.cumulativeCost.toFixed(2),
          ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'tron-resource-projection.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },

      shareUrl: () => {
        const state = get();
        const params = new URLSearchParams({
          rt: state.recipientType,
          st: state.startTpd.toString(),
          di: state.dailyInc.toString(),
          d: state.days.toString(),
          ept: state.energyPerTx.toString(),
          bpt: state.bwPerTx.toString(),
          bptr: state.bwPerTrx.toString(),
          sc: state.sigCount.toString(),
          tp: state.trxPrice.toString(),
          uc: state.usdtContract,
          ep: state.endpoint,
          ae: (state.availEnergy || '').toString(),
          ab: (state.availBandwidth || '').toString(),
        });

        const shareUrl = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
        
        if (navigator.clipboard) {
          navigator.clipboard.writeText(shareUrl);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = shareUrl;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
      },

      reset: () => {
        set(DEFAULT_STATE);
        get().compute();
      },
    }),
    {
      name: 'tron-resource-saver',
      partialize: (state) => ({
        recipientType: state.recipientType,
        startTpd: state.startTpd,
        dailyInc: state.dailyInc,
        days: state.days,
        energyPerTx: state.energyPerTx,
        bwPerTx: state.bwPerTx,
        bwPerTrx: state.bwPerTrx,
        sigCount: state.sigCount,
        trxPrice: state.trxPrice,
        usdtContract: state.usdtContract,
        endpoint: state.endpoint,
        apiKey: state.apiKey,
        availEnergy: state.availEnergy,
        availBandwidth: state.availBandwidth,
      }),
    }
  )
);

// Initialize the store
export const initializeStore = () => {
  const store = useAppStore.getState();
  
  // Load cached chain parameters on init
  const cached = getCachedChainParameters();
  if (cached) {
    store.set({ 
      energyUnitSun: cached.energyUnitSun, 
      bandwidthUnitSun: cached.bandwidthUnitSun 
    });
  }
  
  // Load cached TRX price on init
  const cachedPrice = getCachedTrxPrice();
  if (cachedPrice) {
    store.set({ trxPrice: cachedPrice });
  }
  
  // Compute initial projections
  store.compute();
  
  // Fetch fresh data
  store.fetchChainParams();
  store.fetchTrxPrice();
};
